import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
// ScrollArea removed for proper navigation
import {
  QrCode,
  Book,
  Shield,
  Copy,
  Check,
  FileText,
  Key,
  Image as ImageIcon,
  Settings,
  ChevronRight,
  Globe,
  Palette,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/modules/Navbar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CodeBlock = ({ code, language = "bash" }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group">
      <pre className="code-block text-zinc-300 whitespace-pre-wrap break-all">
        <code>{code}</code>
      </pre>
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 p-2 rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white transition-all opacity-0 group-hover:opacity-100"
      >
        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  );
};

const sections = [
  { id: "overview", label: "Overview", icon: Book },
  { id: "authentication", label: "Authentication", icon: Key },
  { id: "ip-whitelist", label: "IP Whitelist", icon: Globe },
  { id: "rate-limiting", label: "Rate Limiting", icon: Shield },
  { id: "generate-qr", label: "Generate QR", icon: QrCode },
  { id: "generate-qr-base64", label: "Generate QR (Base64)", icon: ImageIcon },
  { id: "generate-pdf-qr", label: "Generate PDF with QR", icon: FileText },
  { id: "style-modes", label: "Style Modes", icon: Palette },
  { id: "parameters", label: "Parameters", icon: Settings },
  { id: "errors", label: "Error Handling", icon: AlertTriangle },
];

export default function DocsPage() {
  const [activeSection, setActiveSection] = useState("overview");

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      // Use scrollIntoView for smooth scrolling within the page
      element.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Header */}
      <Navbar />

      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-[250px_1fr] gap-8">
          {/* Sidebar Navigation */}
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <h3 className="text-sm font-semibold text-zinc-400 mb-4 uppercase tracking-wider">Documentation</h3>
              <nav className="space-y-1">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`w-full text-left px-4 py-2 rounded-md flex items-center gap-2 transition-all ${
                      activeSection === section.id
                        ? "bg-primary-500/20 text-primary-400 border-l-2 border-primary-500"
                        : "text-zinc-400 hover:text-white hover:bg-zinc-800"
                    }`}
                  >
                    <section.icon className="w-4 h-4" />
                    {section.label}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <main>
              <div className="prose prose-invert max-w-none">
                {/* Overview */}
                <section id="overview" className="mb-12">
                  <h1 className="text-3xl font-bold text-white mb-4">QRForge API Documentation v2.0</h1>
                  <p className="text-zinc-400 mb-6">
                    QRForge API allows you to generate customizable QR codes with artistic styles and embed them into PDF documents.
                  </p>

                  <div className="tech-border rounded-lg p-6 bg-dark-foreground mb-6">
                    <h3 className="text-lg font-semibold text-white mb-2">Base URL</h3>
                    <code className="text-primary-400 font-mono">{BACKEND_URL}/api</code>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="tech-border rounded-lg p-4 bg-dark-foreground">
                      <h4 className="font-semibold text-white mb-2">QR Features</h4>
                      <ul className="text-sm text-zinc-400 space-y-1">
                        <li>• Basic & Artistic styles</li>
                        <li>• Multi-color support</li>
                        <li>• Custom corner styles</li>
                        <li>• Logo embedding</li>
                        <li>• Background images</li>
                        <li>• PDF integration</li>
                      </ul>
                    </div>
                    <div className="tech-border rounded-lg p-4 bg-dark-foreground">
                      <h4 className="font-semibold text-white mb-2">Security Features</h4>
                      <ul className="text-sm text-zinc-400 space-y-1">
                        <li>• API key authentication</li>
                        <li>• Domain whitelisting</li>
                        <li>• Token expiration</li>
                        <li>• Usage tracking</li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* Authentication */}
                <section id="authentication" className="mb-12">
                  <h2 className="text-2xl font-bold text-white mb-4">Authentication</h2>
                  <p className="text-zinc-400 mb-4">All API endpoints require authentication via API key:</p>

                  <CodeBlock code={`X-API-Key: qf_your_api_key_here`} />

                  <div className="mt-4 p-4 rounded-lg bg-primary-500/10 border border-primary-500/30">
                    <p className="text-sm text-primary-400">
                      <strong>How to get an API key:</strong>
                    </p>
                    <ol className="text-sm text-primary-400/80 mt-2 list-decimal list-inside space-y-1">
                      <li>Register at <code>/login</code></li>
                      <li>Purchase a package from the dashboard</li>
                      <li>After payment approval, tokens are automatically created</li>
                      <li>Optionally configure IP whitelist for security</li>
                    </ol>
                  </div>
                </section>

                {/* IP Whitelist */}
                <section id="ip-whitelist" className="mb-12">
                  <h2 className="text-2xl font-bold text-white mb-4">IP Whitelisting</h2>
                  <p className="text-zinc-400 mb-4">
                    For security, you can restrict API access to specific IP addresses. This is optional - if no IPs are whitelisted, all IPs are allowed.
                  </p>

                  <div className="tech-border rounded-lg p-4 bg-dark-foreground mb-4">
                    <h4 className="font-semibold text-white mb-2">How it works</h4>
                    <ul className="text-sm text-zinc-400 space-y-1">
                      <li>• Add IP addresses via the dashboard</li>
                      <li>• Supports individual IPs (e.g., <code>192.168.1.1</code>)</li>
                      <li>• Supports CIDR ranges (e.g., <code>192.168.1.0/24</code>)</li>
                      <li>• Empty whitelist = allow all IPs</li>
                      <li>• Requests from non-whitelisted IPs return 403 Forbidden</li>
                    </ul>
                  </div>

                  <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                    <p className="text-sm text-green-400">
                      <strong>Note:</strong> Unlike domain whitelisting, IP whitelisting is optional. If you leave it empty, your API will accept requests from any IP address.
                    </p>
                  </div>
                </section>

                {/* Rate Limiting */}
                <section id="rate-limiting" className="mb-12">
                  <h2 className="text-2xl font-bold text-white mb-4">Rate Limiting</h2>
                  <p className="text-zinc-400 mb-4">
                    API usage is limited based on your subscription package. Each token has a monthly quota of API hits.
                  </p>

                  <div className="tech-border rounded-lg p-4 bg-dark-foreground mb-4">
                    <h4 className="font-semibold text-white mb-2">How it works</h4>
                    <ul className="text-sm text-zinc-400 space-y-1">
                      <li>• Each API call counts as 1 hit</li>
                      <li>• Quota resets every 30 days from token activation</li>
                      <li>• Check your usage in the dashboard</li>
                      <li>• Some packages offer unlimited hits (0 = unlimited)</li>
                    </ul>
                  </div>

                  <h4 className="text-lg font-semibold text-white mb-2">Rate Limit Response</h4>
                  <p className="text-zinc-400 text-sm mb-3">When quota is exceeded:</p>
                  <CodeBlock
                    code={`HTTP/1.1 429 Too Many Requests
Content-Type: application/json

{
  "detail": "API rate limit exceeded. Used: 10000/10000. Resets on: 2024-02-15"
}`}
                  />
                </section>

                {/* Generate QR */}
                <section id="generate-qr" className="mb-12">
                  <h2 className="text-2xl font-bold text-white mb-4">Generate QR Code</h2>
                  <p className="text-zinc-400 mb-4">Generate a QR code image (PNG format).</p>

                  <div className="mb-4">
                    <span className="inline-block px-2 py-1 rounded text-xs font-mono bg-green-500/20 text-green-400 mr-2">POST</span>
                    <code className="text-zinc-300">/api/generate-qr</code>
                  </div>

                  <h4 className="text-lg font-semibold text-white mb-2">Request Body</h4>
                  <CodeBlock
                    code={`{
  "content": "https://example.com",
  "fg_color": "#000000",
  "bg_color": "#FFFFFF",
  "size": 150,
  "style_mode": "basic",
  "eye_frame_shape": "square",
  "eye_ball_shape": "square",
  "enable_gradient": false,
  "gradient_start": "#1E3A8A",
  "gradient_end": "#92400E",
  "gradient_direction": "diagonal",
  "eye_color": null,
  "logo_base64": null,
  "bg_image_base64": null
}`}
                  />

                  <h4 className="text-lg font-semibold text-white mt-6 mb-2">cURL Example</h4>
                  <CodeBlock
                    code={`curl -X POST "${BACKEND_URL}/api/generate-qr" \\
  -H "X-API-Key: qf_your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "content": "https://example.com",
    "style_mode": "basic",
    "eye_frame_shape": "drop",
    "eye_ball_shape": "circle",
    "enable_gradient": true,
    "gradient_start": "#FF6B6B",
    "gradient_end": "#4ECDC4",
    "size": 300
  }' \\
  -o qr_code.png`}
                  />

                  <h4 className="text-lg font-semibold text-white mt-6 mb-2">Response</h4>
                  <p className="text-zinc-400 mb-2">Returns PNG image binary data.</p>
                  <CodeBlock code={`Content-Type: image/png`} />
                </section>

                {/* Generate QR Base64 */}
                <section id="generate-qr-base64" className="mb-12">
                  <h2 className="text-2xl font-bold text-white mb-4">Generate QR Code (Base64)</h2>
                  <p className="text-zinc-400 mb-4">Generate a QR code as base64 encoded string.</p>

                  <div className="mb-4">
                    <span className="inline-block px-2 py-1 rounded text-xs font-mono bg-green-500/20 text-green-400 mr-2">POST</span>
                    <code className="text-zinc-300">/api/generate-qr-base64</code>
                  </div>

                  <h4 className="text-lg font-semibold text-white mb-2">Response</h4>
                  <CodeBlock
                    code={`{
  "qr_base64": "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAA..."
}`}
                  />
                </section>

                {/* Generate PDF with QR */}
                <section id="generate-pdf-qr" className="mb-12">
                  <h2 className="text-2xl font-bold text-white mb-4">Generate PDF with QR Code</h2>
                  <p className="text-zinc-400 mb-4">Upload a PDF and embed a QR code at a specified position.</p>

                  <div className="mb-4">
                    <span className="inline-block px-2 py-1 rounded text-xs font-mono bg-green-500/20 text-green-400 mr-2">POST</span>
                    <code className="text-zinc-300">/api/generate-pdf-qr</code>
                  </div>

                  <h4 className="text-lg font-semibold text-white mb-2">Request (multipart/form-data)</h4>
                  <CodeBlock
                    code={`curl -X POST "${BACKEND_URL}/api/generate-pdf-qr" \\
  -H "X-API-Key: qf_your_api_key" \\
  -F "pdf_file=@document.pdf" \\
  -F "content=https://example.com" \\
  -F "page_number=1" \\
  -F "position_type=bottom-right" \\
  -F "x=50" \\
  -F "y=50" \\
  -F "fg_color=#000000" \\
  -F "bg_color=#FFFFFF" \\
  -F "size=150" \\
  -F "style_mode=basic" \\
  -F "eye_frame_shape=drop" \\
  -F "eye_ball_shape=circle" \\
  -F "enable_gradient=true" \\
  -F "gradient_start=#1E3A8A" \\
  -F "gradient_end=#92400E" \\
  -F "gradient_direction=diagonal" \\
  -F "eye_color=#FF0000" \\
  -o output.pdf`}
                  />
                  
                  <h4 className="text-lg font-semibold text-white mt-6 mb-2">Position Types</h4>
                  <div className="tech-border rounded-lg p-4 bg-dark-foreground">
                    <div className="grid grid-cols-3 gap-2 text-sm text-zinc-400">
                      <div>• <code className="text-primary-400">custom</code> - Use x, y coordinates</div>
                      <div>• <code className="text-primary-400">top-left</code></div>
                      <div>• <code className="text-primary-400">top-center</code></div>
                      <div>• <code className="text-primary-400">top-right</code></div>
                      <div>• <code className="text-primary-400">center-left</code></div>
                      <div>• <code className="text-primary-400">center</code></div>
                      <div>• <code className="text-primary-400">center-right</code></div>
                      <div>• <code className="text-primary-400">bottom-left</code></div>
                      <div>• <code className="text-primary-400">bottom-center</code></div>
                      <div>• <code className="text-primary-400">bottom-right</code></div>
                    </div>
                  </div>
                </section>

                {/* Style Modes */}
                <section id="style-modes" className="mb-12">
                  <h2 className="text-2xl font-bold text-white mb-4">Style Modes</h2>
                  <p className="text-zinc-400 mb-4">
                    Create visually stunning QR codes with different styles. All modes support custom eye shapes and optional gradient coloring.
                  </p>

                  <div className="grid md:grid-cols-2 gap-4 mb-6">
                    <div className="tech-border rounded-lg p-4 bg-dark-foreground">
                      <h4 className="font-semibold text-white mb-2">Basic Mode</h4>
                      <ul className="text-sm text-zinc-400 space-y-1">
                        <li>• Single foreground color</li>
                        <li>• Custom eye frame & ball shapes</li>
                        <li>• Optional gradient colors</li>
                        <li>• Optional separate eye color</li>
                        <li>• Clean, professional look</li>
                      </ul>
                    </div>
                    <div className="tech-border rounded-lg p-4 bg-dark-foreground">
                      <h4 className="font-semibold text-white mb-2">Artistic Mode</h4>
                      <ul className="text-sm text-zinc-400 space-y-1">
                        <li>• Circular dot modules</li>
                        <li>• Custom eye frame & ball shapes</li>
                        <li>• Optional gradient colors</li>
                        <li>• Optional separate eye color</li>
                        <li>• Modern, creative look</li>
                      </ul>
                    </div>
                  </div>

                  <h4 className="text-lg font-semibold text-white mb-2">Eye Frame Shapes</h4>
                  <p className="text-zinc-400 text-sm mb-3">
                    Customize the outer frame of finder patterns (the 3 large squares in corners):
                  </p>
                  <div className="tech-border rounded-lg p-4 bg-dark-foreground mb-6">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm text-zinc-400">
                      <div>• <code className="text-primary-400">square</code> - Default square</div>
                      <div>• <code className="text-primary-400">rounded</code> - All corners rounded</div>
                      <div>• <code className="text-primary-400">circle</code> - Circular shape</div>
                      <div>• <code className="text-primary-400">leaf</code> - Diagonal corners</div>
                      <div>• <code className="text-primary-400">drop</code> - Teardrop shape pointing to center</div>
                      <div>• <code className="text-primary-400">dotted</code> - Dotted border</div>
                    </div>
                  </div>

                  <h4 className="text-lg font-semibold text-white mb-2">Eye Ball Shapes</h4>
                  <p className="text-zinc-400 text-sm mb-3">
                    Customize the inner element of finder patterns:
                  </p>
                  <div className="tech-border rounded-lg p-4 bg-dark-foreground mb-6">
                    <div className="grid grid-cols-2 gap-2 text-sm text-zinc-400">
                      <div>• <code className="text-primary-400">square</code> - Default square inner</div>
                      <div>• <code className="text-primary-400">circle</code> - Circular inner</div>
                    </div>
                  </div>

                  <h4 className="text-lg font-semibold text-white mb-2">Example: Drop Frame + Circle Ball</h4>
                  <CodeBlock
                    code={`{
  "content": "https://example.com",
  "style_mode": "basic",
  "eye_frame_shape": "drop",
  "eye_ball_shape": "circle",
  "enable_gradient": true,
  "gradient_start": "#1E3A8A",
  "gradient_end": "#92400E",
  "gradient_direction": "diagonal",
  "eye_color": "#1E3A8A",
  "bg_color": "#FFFFFF",
  "size": 300
}`}
                  />

                  <h4 className="text-lg font-semibold text-white mt-6 mb-2">Artistic Mode with Gradient</h4>
                  <CodeBlock
                    code={`{
  "content": "https://example.com",
  "style_mode": "artistic",
  "eye_frame_shape": "rounded",
  "eye_ball_shape": "circle",
  "enable_gradient": true,
  "gradient_start": "#FF6B6B",
  "gradient_end": "#4ECDC4",
  "gradient_direction": "radial",
  "bg_color": "#FFFFFF",
  "size": 300
}`}
                  />
                </section>

                {/* Parameters */}
                <section id="parameters" className="mb-12">
                  <h2 className="text-2xl font-bold text-white mb-4">Parameters Reference</h2>

                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-zinc-800">
                          <th className="text-left py-3 text-zinc-400 font-medium">Parameter</th>
                          <th className="text-left py-3 text-zinc-400 font-medium">Type</th>
                          <th className="text-left py-3 text-zinc-400 font-medium">Default</th>
                          <th className="text-left py-3 text-zinc-400 font-medium">Description</th>
                        </tr>
                      </thead>
                      <tbody className="text-zinc-300">
                        <tr className="border-b border-zinc-800/50">
                          <td className="py-3 font-mono text-primary-400">content</td>
                          <td className="py-3">string</td>
                          <td className="py-3">required</td>
                          <td className="py-3 text-zinc-400">Text or URL to encode</td>
                        </tr>
                        <tr className="border-b border-zinc-800/50">
                          <td className="py-3 font-mono text-primary-400">style_mode</td>
                          <td className="py-3">string</td>
                          <td className="py-3">basic</td>
                          <td className="py-3 text-zinc-400">basic or artistic</td>
                        </tr>
                        <tr className="border-b border-zinc-800/50">
                          <td className="py-3 font-mono text-primary-400">eye_frame_shape</td>
                          <td className="py-3">string</td>
                          <td className="py-3">square</td>
                          <td className="py-3 text-zinc-400">Finder pattern outer shape: square, rounded, circle, leaf, drop, dotted</td>
                        </tr>
                        <tr className="border-b border-zinc-800/50">
                          <td className="py-3 font-mono text-primary-400">eye_ball_shape</td>
                          <td className="py-3">string</td>
                          <td className="py-3">square</td>
                          <td className="py-3 text-zinc-400">Finder pattern inner shape: square, circle</td>
                        </tr>
                        <tr className="border-b border-zinc-800/50">
                          <td className="py-3 font-mono text-primary-400">enable_gradient</td>
                          <td className="py-3">boolean</td>
                          <td className="py-3">false</td>
                          <td className="py-3 text-zinc-400">Enable gradient coloring for data modules</td>
                        </tr>
                        <tr className="border-b border-zinc-800/50">
                          <td className="py-3 font-mono text-primary-400">gradient_start</td>
                          <td className="py-3">string</td>
                          <td className="py-3">#1E3A8A</td>
                          <td className="py-3 text-zinc-400">Gradient start color (hex)</td>
                        </tr>
                        <tr className="border-b border-zinc-800/50">
                          <td className="py-3 font-mono text-primary-400">gradient_end</td>
                          <td className="py-3">string</td>
                          <td className="py-3">#92400E</td>
                          <td className="py-3 text-zinc-400">Gradient end color (hex)</td>
                        </tr>
                        <tr className="border-b border-zinc-800/50">
                          <td className="py-3 font-mono text-primary-400">gradient_direction</td>
                          <td className="py-3">string</td>
                          <td className="py-3">diagonal</td>
                          <td className="py-3 text-zinc-400">vertical, horizontal, diagonal, radial</td>
                        </tr>
                        <tr className="border-b border-zinc-800/50">
                          <td className="py-3 font-mono text-primary-400">eye_color</td>
                          <td className="py-3">string</td>
                          <td className="py-3">null</td>
                          <td className="py-3 text-zinc-400">Separate solid color for finder patterns (when gradient enabled)</td>
                        </tr>
                        <tr className="border-b border-zinc-800/50">
                          <td className="py-3 font-mono text-primary-400">fg_color</td>
                          <td className="py-3">string</td>
                          <td className="py-3">#000000</td>
                          <td className="py-3 text-zinc-400">Primary/Foreground color (hex)</td>
                        </tr>
                        <tr className="border-b border-zinc-800/50">
                          <td className="py-3 font-mono text-primary-400">bg_color</td>
                          <td className="py-3">string</td>
                          <td className="py-3">#FFFFFF</td>
                          <td className="py-3 text-zinc-400">Background color (hex)</td>
                        </tr>
                        <tr className="border-b border-zinc-800/50">
                          <td className="py-3 font-mono text-primary-400">size</td>
                          <td className="py-3">integer</td>
                          <td className="py-3">150</td>
                          <td className="py-3 text-zinc-400">QR code size in pixels</td>
                        </tr>
                        <tr className="border-b border-zinc-800/50">
                          <td className="py-3 font-mono text-primary-400">logo_base64</td>
                          <td className="py-3">string</td>
                          <td className="py-3">null</td>
                          <td className="py-3 text-zinc-400">Base64 encoded logo image to embed in center</td>
                        </tr>
                        <tr className="border-b border-zinc-800/50">
                          <td className="py-3 font-mono text-primary-400">bg_image_base64</td>
                          <td className="py-3">string</td>
                          <td className="py-3">null</td>
                          <td className="py-3 text-zinc-400">Base64 encoded background image</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <h4 className="text-lg font-semibold text-white mt-6 mb-2">PDF-Only Parameters</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-zinc-800">
                          <th className="text-left py-3 text-zinc-400 font-medium">Parameter</th>
                          <th className="text-left py-3 text-zinc-400 font-medium">Type</th>
                          <th className="text-left py-3 text-zinc-400 font-medium">Default</th>
                          <th className="text-left py-3 text-zinc-400 font-medium">Description</th>
                        </tr>
                      </thead>
                      <tbody className="text-zinc-300">
                        <tr className="border-b border-zinc-800/50">
                          <td className="py-3 font-mono text-primary-400">pdf_file</td>
                          <td className="py-3">file</td>
                          <td className="py-3">required</td>
                          <td className="py-3 text-zinc-400">PDF file to embed QR code</td>
                        </tr>
                        <tr className="border-b border-zinc-800/50">
                          <td className="py-3 font-mono text-primary-400">page_number</td>
                          <td className="py-3">integer</td>
                          <td className="py-3">1</td>
                          <td className="py-3 text-zinc-400">Target page number</td>
                        </tr>
                        <tr className="border-b border-zinc-800/50">
                          <td className="py-3 font-mono text-primary-400">position_type</td>
                          <td className="py-3">string</td>
                          <td className="py-3">custom</td>
                          <td className="py-3 text-zinc-400">custom, top-left, top-center, top-right, center-left, center, center-right, bottom-left, bottom-center, bottom-right</td>
                        </tr>
                        <tr className="border-b border-zinc-800/50">
                          <td className="py-3 font-mono text-primary-400">x, y</td>
                          <td className="py-3">integer</td>
                          <td className="py-3">50</td>
                          <td className="py-3 text-zinc-400">Coordinates for custom position (pixels from top-left)</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </section>

                {/* Error Handling */}
                <section id="errors" className="mb-12">
                  <h2 className="text-2xl font-bold text-white mb-4">Error Handling</h2>

                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-zinc-800">
                          <th className="text-left py-3 text-zinc-400 font-medium">Code</th>
                          <th className="text-left py-3 text-zinc-400 font-medium">Description</th>
                        </tr>
                      </thead>
                      <tbody className="text-zinc-300">
                        <tr className="border-b border-zinc-800/50">
                          <td className="py-3 font-mono text-red-400">401</td>
                          <td className="py-3 text-zinc-400">Invalid or missing API key</td>
                        </tr>
                        <tr className="border-b border-zinc-800/50">
                          <td className="py-3 font-mono text-red-400">403</td>
                          <td className="py-3 text-zinc-400">API key disabled, expired, or domain not whitelisted</td>
                        </tr>
                        <tr className="border-b border-zinc-800/50">
                          <td className="py-3 font-mono text-red-400">400</td>
                          <td className="py-3 text-zinc-400">Invalid request parameters</td>
                        </tr>
                        <tr className="border-b border-zinc-800/50">
                          <td className="py-3 font-mono text-red-400">500</td>
                          <td className="py-3 text-zinc-400">Server error</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <h4 className="text-lg font-semibold text-white mt-6 mb-2">Error Response Format</h4>
                  <CodeBlock
                    code={`{
  "detail": "Domain 'unauthorized.com' is not whitelisted for this API key"
}`}
                  />
                </section>
              </div>
          </main>
        </div>
      </div>
    </div>
  );
}
